import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate, Link } from "react-router-dom";
import { ShieldAlert, Check, X, Loader2, Eye, Activity, Users, Flag, Database, FileUp, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import AdminLocationEditModal from "../components/AdminLocationEditModal";

function isAdmin(user) {
  return !!(user && user.is_admin);
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AdminModeration() {
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // location doc to inspect/edit

  const pending = useQuery({
    queryKey: ["admin-pending"],
    enabled: !!user && isAdmin(user),
    queryFn: async () => (await api.get("/admin/locations/pending")).data,
  });

  const approveMut = useMutation({
    mutationFn: (id) => api.post(`/admin/locations/${id}/approve`),
    onSuccess: (_d, id) => {
      toast.success(`Approved · ${id}`);
      qc.invalidateQueries({ queryKey: ["admin-pending"] });
      qc.invalidateQueries({ queryKey: ["admin-locations"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't approve"),
  });

  const rejectMut = useMutation({
    mutationFn: (id) => api.post(`/admin/locations/${id}/reject`),
    onSuccess: (_d, id) => {
      toast.success(`Rejected · ${id}`);
      qc.invalidateQueries({ queryKey: ["admin-pending"] });
      qc.invalidateQueries({ queryKey: ["admin-locations"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't reject"),
  });

  if (loading) return <div className="p-8 text-brand-subtle">Loading…</div>;
  if (!user || !isAdmin(user)) return <Navigate to="/" replace />;

  const items = pending.data?.items || [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-moderation-page">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="td-section-label">Admin</div>
          <h1 className="font-heading text-4xl font-semibold text-brand-text mt-1 flex items-center gap-3">
            <ShieldAlert size={28} className="text-brand-accent" /> Pending approvals
          </h1>
          <p className="text-brand-subtle text-sm mt-1">
            User-submitted trails &amp; edits waiting on a green light.
            Approved items go live on the public map immediately.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/locations" data-testid="mod-to-locations" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Database size={14} /> All locations
          </Link>
          <Link to="/admin/trails/new" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <FileUp size={14} /> Add trail
          </Link>
          <Link to="/admin/telemetry" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Activity size={14} /> Telemetry
          </Link>
          <Link to="/admin/users" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Users size={14} /> Users
          </Link>
          <Link to="/admin/reports" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Flag size={14} /> Reports
          </Link>
        </div>
      </header>

      <div className="td-card p-5" data-testid="moderation-queue">
        <div className="flex items-center justify-between mb-3">
          <div className="td-section-label">Queue · {items.length}</div>
          <button onClick={() => pending.refetch()} className="text-xs text-brand-subtle hover:text-brand-text">Refresh</button>
        </div>

        {pending.isLoading ? (
          <div className="py-12 text-center text-brand-subtle"><Loader2 size={18} className="animate-spin inline" /> Loading queue…</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center" data-testid="moderation-empty">
            <p className="text-brand-text font-medium">All clear — nothing to review.</p>
            <p className="text-xs text-brand-subtle mt-1">Submissions from non-admin users will appear here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-brand-border/60">
            {items.map((loc) => (
              <li key={loc.location_id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`pending-row-${loc.location_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-brand-text truncate">{loc.name}</div>
                  <div className="text-xs text-brand-subtle flex flex-wrap items-center gap-2 mt-0.5">
                    <span className="inline-flex items-center gap-1"><MapPin size={11} /> {loc.region || "United Kingdom"}</span>
                    <span className="opacity-50">·</span>
                    <span>{loc.type}</span>
                    <span className="opacity-50">·</span>
                    <span>uploaded by {loc.uploaded_by || "anonymous"} · {fmt(loc.created_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link
                    to={`/location/${loc.location_id}`}
                    data-testid={`pending-view-${loc.location_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="td-btn-secondary !py-1.5 !px-3 !text-xs whitespace-nowrap"
                    title="Preview public page (will not be visible to walkers until approved)"
                  >
                    <Eye size={12} /> View
                  </Link>
                  <button
                    data-testid={`pending-edit-${loc.location_id}`}
                    onClick={() => setEditing(loc)}
                    className="td-btn-secondary !py-1.5 !px-3 !text-xs whitespace-nowrap"
                  >
                    Edit
                  </button>
                  <button
                    data-testid={`pending-approve-${loc.location_id}`}
                    onClick={() => approveMut.mutate(loc.location_id)}
                    disabled={approveMut.isPending}
                    className="td-btn-primary !py-1.5 !px-3 !text-xs whitespace-nowrap"
                  >
                    <Check size={12} /> Approve
                  </button>
                  <button
                    data-testid={`pending-reject-${loc.location_id}`}
                    onClick={() => rejectMut.mutate(loc.location_id)}
                    disabled={rejectMut.isPending}
                    className="td-btn-secondary !py-1.5 !px-3 !text-xs whitespace-nowrap !text-brand-error !border-brand-error/40"
                  >
                    <X size={12} /> Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && <AdminLocationEditModal location={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
